import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ScenarioIncident } from '../../types/ScenarioManagement';
import { StandardButton } from '../ui';
import { IncidentGridCard } from './IncidentGridCard';
import { IncidentTableRow } from './IncidentTableRow';

interface IncidentListProps {
  incidents: ScenarioIncident[];
  onEdit: (incident: ScenarioIncident) => void;
  onDelete: (incidentId: string) => void;
  onToggle: (incidentId: string, enabled: boolean) => void;
  onReorder: (incidentId: string, newOrder: number) => void;
  readOnly?: boolean;
}

export const IncidentList: React.FC<IncidentListProps> = ({
  incidents,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
  readOnly = false
}) => {
  const { isDark } = useTheme();
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  const getSeverityColor = (severity: string) => {
    if (isDark) {
      switch (severity) {
        case 'low': return 'text-green-400 bg-green-900/30';
        case 'medium': return 'text-yellow-400 bg-yellow-900/30';
        case 'high': return 'text-orange-400 bg-orange-900/30';
        case 'critical': return 'text-red-400 bg-red-900/30';
        default: return 'text-slate-400 bg-slate-900/30';
      }
    } else {
      switch (severity) {
        case 'low': return 'text-green-600 bg-green-100';
        case 'medium': return 'text-yellow-600 bg-yellow-100';
        case 'high': return 'text-orange-600 bg-orange-100';
        case 'critical': return 'text-red-600 bg-red-100';
        default: return 'text-gray-600 bg-gray-100';
      }
    }
  };

  const formatTimeLimit = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };


  if (incidents.length === 0) {
    return (
      <div className={`text-center py-12 rounded-lg border-2 border-dashed ${
        isDark ? 'border-white/10' : 'border-gray-300'
      }`}>
        <div className="text-6xl mb-4">🚨</div>
        <h3 className={`text-lg font-medium mb-2 ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          No Incidents Configured
        </h3>
        <p className={isDark ? 'text-slate-300' : 'text-gray-500'}>
          Create your first incident to build challenging scenarios
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex justify-end">
        <div className={`flex ${isDark ? 'bg-white/[0.05]' : 'bg-gray-100'} rounded-lg p-1`}>
          <StandardButton
            type="button"
            onClick={() => setViewMode('grid')}
            variant={viewMode === 'grid' ? 'primary' : 'ghost'}
            size="small"
            title="Grid View"
            className="!px-3 !py-2"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/>
            </svg>
          </StandardButton>
          <StandardButton
            type="button"
            onClick={() => setViewMode('table')}
            variant={viewMode === 'table' ? 'primary' : 'ghost'}
            size="small"
            title="Table View"
            className="!px-3 !py-2"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 3h18v2H3V3zm0 4h18v2H3V7zm0 4h18v2H3v-2zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/>
            </svg>
          </StandardButton>
        </div>
      </div>

      {viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {incidents.map((incident, index) => (
            <IncidentGridCard
              key={incident.id}
              incident={incident}
              index={index}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggle={onToggle}
              onReorder={onReorder}
              readOnly={readOnly}
              totalIncidents={incidents.length}
              getSeverityColor={getSeverityColor}
              formatTimeLimit={formatTimeLimit}
            />
          ))}
        </div>
      ) : (
        /* Table View */
        <div className={`rounded-lg shadow-sm border overflow-hidden ${
          isDark 
            ? 'bg-white/[0.03] border-white/10' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${
              isDark ? 'divide-white/10' : 'divide-gray-200'
            }`}>
              <thead className={isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Order
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Incident
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Severity
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Time Limit
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Category
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Responses
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                isDark 
                  ? 'bg-white/[0.01] divide-white/10' 
                  : 'bg-white divide-gray-200'
              }`}>
                {incidents.map((incident, index) => (
                  <IncidentTableRow
                    key={incident.id}
                    incident={incident}
                    index={index}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onToggle={onToggle}
                    onReorder={onReorder}
                    readOnly={readOnly}
                    totalIncidents={incidents.length}
                    getSeverityColor={getSeverityColor}
                    formatTimeLimit={formatTimeLimit}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};